import type { ChangeEvent } from 'react';
import type { AccessCodePolicy } from 'src/generated/graphql-manager';

import {
  Checkbox,
  CopyToClipboard,
  InputDescription,
  SelectionInputLayout,
  TextInput,
  notification,
  toast,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useUpdateAccessCodePolicyMutation } from 'src/generated/graphql-manager';

import { validateAccessCode } from '../validators';

type Props = {
  accessCodePolicy?: AccessCodePolicy;
  isReadOnly: boolean;
  formDisabled?: boolean;
  refetch: () => void;
};

export function AccessCodePolicy({ accessCodePolicy, isReadOnly, formDisabled, refetch }: Props) {
  const { t } = useTranslation();
  const { webcastId = '' } = useParams();

  const [customAccessCode, setCustomAccessCode] = useState(accessCodePolicy?.accessCode || '');
  const [customAccessCodeValidationError, setCustomAccessCodeValidationError] = useState<string>();

  const accessCodeEnabled = accessCodePolicy?.enabled;
  const accessCode = accessCodePolicy?.accessCode || '';

  const [updateAccessCodePolicyMutation, { loading: updateAccessCodePolicyLoading }] =
    useUpdateAccessCodePolicyMutation();

  const updateAccessCodePolicy = ({
    enabled = Boolean(accessCodePolicy?.enabled),
    accessCode = accessCodePolicy?.accessCode || '',
  }: { enabled?: AccessCodePolicy['enabled']; accessCode?: AccessCodePolicy['accessCode'] } = {}) => {
    if (updateAccessCodePolicyLoading) return;

    updateAccessCodePolicyMutation({
      variables: {
        input: {
          webcastId,
          enabled,
          accessCode,
        },
      },
      optimisticResponse: {
        updateAccessCodePolicy: {
          __typename: 'UpdateAccessCodePolicySuccess',
          accessCodePolicy: {
            __typename: 'AccessCodePolicy',
            enabled,
            accessCode,
          },
        },
      },
      update: (_, { data }) => {
        if (data?.updateAccessCodePolicy.__typename === 'ValidationErrors') {
          setCustomAccessCodeValidationError(data.updateAccessCodePolicy?.errors?.[0]?.message);
        }

        if (data?.updateAccessCodePolicy.__typename === 'UpdateAccessCodePolicySuccess') {
          setCustomAccessCodeValidationError(undefined);
        }
      },
      onCompleted: (data) => {
        refetch();

        if (data?.updateAccessCodePolicy.__typename === 'UpdateAccessCodePolicySuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  const handleAccessCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCustomAccessCodeValidationError(undefined);
    setCustomAccessCode(event.target.value.trim());
  };

  const saveCustomAccessCode = () => {
    const error = validateAccessCode(customAccessCode);

    if (error) return setCustomAccessCodeValidationError(error);

    updateAccessCodePolicy({ accessCode: customAccessCode });
  };

  useEffect(() => {
    if (accessCodeEnabled) setCustomAccessCode(accessCode);
  }, [accessCodeEnabled, accessCode]);

  return (
    <SelectionInputLayout
      variant="ghost"
      checked={accessCodeEnabled}
      disabled={formDisabled}
      inputElement={
        <Checkbox
          id="access-code-policy"
          data-testid="access-code-policy-checkbox"
          checked={accessCodeEnabled}
          disabled={formDisabled}
          onChange={() => updateAccessCodePolicy({ enabled: !accessCodeEnabled })}
        >
          {t('manager.webcastSetup.eventAccess.accessControlMethods.accessCode.label')}
        </Checkbox>
      }
    >
      <InputDescription
        inputId="access-code-input"
        label={t('manager.webcastSetup.eventAccess.accessControlMethods.accessCode.inputLabel')}
        helpMessage={t('manager.webcastSetup.eventAccess.accessControlMethods.accessCode.hint')}
        errorMessage={customAccessCodeValidationError}
      >
        <TextInput
          data-testid="access-code-input"
          value={customAccessCode}
          onChange={handleAccessCodeChange}
          onBlur={saveCustomAccessCode}
          readOnly={isReadOnly}
        >
          <CopyToClipboard value={customAccessCode} notificationPosition="left" />
        </TextInput>
      </InputDescription>
    </SelectionInputLayout>
  );
}
